import { BookOpen, Plus, MapPin, Clock, Loader2, PenLine, FileText } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { usePastClasses, type PastClass } from '../../hooks/student/usePastClasses';
import { useNotes, useCreateNote, type Note } from '../../hooks/student/useNotes';
import { ROUTES } from '../../constants/routes';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatClassDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86_400_000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function getInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}

function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// ─── Past Class Card ─────────────────────────────────────────────────────────

function PastClassCard({
  cls,
  onWriteNote,
  isCreating,
}: {
  cls: PastClass;
  onWriteNote: () => void;
  isCreating: boolean;
}) {
  const instructors = cls.instructor_names || [];

  return (
    <div className="p-3 hover:bg-white/[0.03] transition-colors">
      <div className="flex items-start gap-2.5">
        {/* Instructor avatar */}
        <div className="shrink-0 mt-0.5">
          {instructors.length > 0 ? (
            <div
              className="w-8 h-8 rounded-full bg-[#9B1421]/30 flex items-center justify-center text-xs font-semibold text-[#EBFFEE]"
              title={instructors[0]}
            >
              {getInitial(instructors[0])}
            </div>
          ) : (
            <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
              <BookOpen className="w-3.5 h-3.5 text-[#A9A3A3]" />
            </div>
          )}
        </div>

        {/* Details */}
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-[#EBFFEE] truncate leading-tight">
            {cls.sport_name ?? 'Class'}
          </p>
          <div className="flex items-center gap-1.5 text-[10px] text-[#A9A3A3] mt-1">
            <Clock className="w-2.5 h-2.5 shrink-0" />
            <span>{formatClassDate(cls.start_time)} {formatTime(cls.start_time)}</span>
          </div>
          {cls.location_name && (
            <div className="flex items-center gap-1.5 text-[10px] text-[#A9A3A3] mt-0.5">
              <MapPin className="w-2.5 h-2.5 shrink-0" />
              <span className="truncate">{cls.location_name}</span>
            </div>
          )}
        </div>

        {/* Write note button */}
        <button
          onClick={onWriteNote}
          disabled={isCreating}
          className="shrink-0 mt-1 flex items-center gap-1 text-[10px] text-[#EBFFEE] bg-[#9B1421]/30 hover:bg-[#9B1421]/50 px-2 py-1 rounded-md font-medium transition-colors disabled:opacity-50"
          title="Write a journal entry about this class"
        >
          {isCreating ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <PenLine className="w-3 h-3" />
          )}
          Note
        </button>
      </div>
    </div>
  );
}

// ─── Recent Note Card ────────────────────────────────────────────────────────

function RecentNoteCard({ note }: { note: Note }) {
  return (
    <Link
      to={ROUTES.STUDENT_JOURNAL}
      className="block p-3 hover:bg-white/[0.03] transition-colors"
    >
      <div className="flex items-start gap-2.5">
        <FileText className="w-4 h-4 text-[#A9A3A3] shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-[#EBFFEE] truncate">{note.title || 'Untitled'}</p>
          <p className="text-[11px] text-[#A9A3A3] line-clamp-1 mt-0.5">{note.body || 'No content'}</p>
          {note.updated_at && (
            <p className="text-[10px] text-[#A9A3A3] mt-1">{timeAgo(note.updated_at)}</p>
          )}
        </div>
      </div>
    </Link>
  );
}

// ─── Journal Widget ──────────────────────────────────────────────────────────

export default function JournalWidget() {
  const { pastClasses, isLoading: classesLoading } = usePastClasses(8);
  const { notes, isLoading: notesLoading } = useNotes();
  const createNote = useCreateNote();
  const navigate = useNavigate();

  const handleNewNote = async () => {
    const result = await createNote.mutateAsync({ title: '', body: '' });
    if (result?.id) {
      navigate(ROUTES.STUDENT_JOURNAL);
    }
  };

  const handleWriteAboutClass = async (cls: PastClass) => {
    const dateStr = cls.start_time
      ? new Date(cls.start_time).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })
      : '';
    const title = `${cls.sport_name || 'Class'} – ${dateStr}`;
    const body = [
      cls.instructor_names?.length ? `Instructor: ${cls.instructor_names.join(', ')}` : '',
      cls.location_name ? `Location: ${cls.location_name}` : '',
      '',
      '## What I worked on\n',
      '## What went well\n',
      '## What to improve\n',
    ].filter(Boolean).join('\n');

    const result = await createNote.mutateAsync({ title, body });
    if (result?.id) {
      navigate(ROUTES.STUDENT_JOURNAL);
    }
  };

  const recentNotes = notes.slice(0, 3);

  return (
    <div className="space-y-4">
      {/* ── New Note Action ────────────────────────────────────── */}
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-xl overflow-hidden">
        <button
          onClick={handleNewNote}
          disabled={createNote.isPending}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors"
        >
          <div className="w-8 h-8 rounded-lg bg-[#9B1421] flex items-center justify-center shrink-0">
            {createNote.isPending ? (
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Plus className="w-4 h-4 text-white" />
            )}
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-[#EBFFEE]">New Journal Entry</p>
            <p className="text-[10px] text-[#A9A3A3]">Capture your training thoughts</p>
          </div>
        </button>
      </div>

      {/* ── Recent Notes ───────────────────────────────────────── */}
      {notesLoading ? null : recentNotes.length > 0 && (
        <div className="bg-[#221F1F] border border-[#504A4A] rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#504A4A]">
            <h3 className="text-xs font-semibold text-[#A9A3A3] uppercase tracking-wider">Recent Notes</h3>
            <Link
              to={ROUTES.STUDENT_JOURNAL}
              className="text-[11px] text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
            >
              View All
            </Link>
          </div>
          <div className="divide-y divide-[#504A4A]/30">
            {recentNotes.map((note) => (
              <RecentNoteCard key={note.id} note={note} />
            ))}
          </div>
        </div>
      )}

      {/* ── Past Classes ───────────────────────────────────────── */}
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-[#504A4A]">
          <h3 className="text-xs font-semibold text-[#A9A3A3] uppercase tracking-wider">Log Your Training</h3>
          <p className="text-[10px] text-[#A9A3A3] mt-0.5">Tap a class to journal about it</p>
        </div>

        {classesLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-[#A9A3A3]" />
          </div>
        ) : pastClasses.length === 0 ? (
          <div className="p-4 text-center">
            <BookOpen className="w-6 h-6 text-[#504A4A] mx-auto mb-1.5" />
            <p className="text-xs text-[#A9A3A3]">No classes attended yet</p>
            <Link
              to={ROUTES.STUDENT_SCHEDULE}
              className="text-[11px] text-[#EBFFEE] hover:text-white transition-colors mt-1 inline-block"
            >
              Browse schedule
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-[#504A4A]/30">
            {pastClasses.map((cls) => (
              <PastClassCard
                key={cls.enrollment_id}
                cls={cls}
                onWriteNote={() => handleWriteAboutClass(cls)}
                isCreating={createNote.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
